import type { TicketsBuildRun } from '../services/tickets-exec.service';
import type { TicketsExecutionRuntime } from '../state/tickets/tickets.models';

type RunItem = NonNullable<TicketsBuildRun['items']>[number];

function pickCurrentRunItem(run: TicketsBuildRun): RunItem | null {
  return (
    (run.items ?? []).find((item) => String(item?.ticketId ?? '') === String(run.currentTicketId ?? '')) ??
    (run.items ?? []).find((item) => item.status === 'awaiting_confirmation') ??
    (run.items ?? []).find((item) => item.status === 'executing') ??
    null
  );
}

function mapWorkUnits(item: RunItem | null): TicketsExecutionRuntime['workUnits'] {
  if (!Array.isArray(item?.workUnits)) return undefined;
  return item.workUnits.map((unit: any) => ({
    id: String(unit?.id ?? '').trim(),
    owner: String(unit?.owner ?? '').trim(),
    repo: String(unit?.repo ?? '').trim(),
    goal: String(unit?.goal ?? '').trim(),
    status: String(unit?.status ?? '').trim(),
    skillId: String(unit?.skillId ?? '').trim() || undefined,
    pullRequest: unit?.pullRequest,
  }));
}

function mapPlannerRepoConfidence(item: RunItem | null): TicketsExecutionRuntime['plannerRepoConfidence'] {
  const raw = (item as any)?.plannerRepoConfidence ?? (item as any)?.resumeDraft?.plannerRepoConfidence;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw.map((row: any) => ({
    owner: String(row?.owner ?? '').trim(),
    repo: String(row?.repo ?? '').trim(),
    skillId: String(row?.skillId ?? '').trim() || undefined,
    verdict: (String(row?.verdict ?? 'unknown').trim() as 'high' | 'med' | 'low' | 'unknown') || 'unknown',
    scorePct: typeof row?.scorePct === 'number' ? row.scorePct : 0,
    reason: String(row?.reason ?? '').trim(),
    positives: typeof row?.positives === 'number' ? row.positives : 0,
    negatives: typeof row?.negatives === 'number' ? row.negatives : 0,
    neutrals: typeof row?.neutrals === 'number' ? row.neutrals : 0,
    computedAt: typeof row?.computedAt === 'number' ? row.computedAt : undefined,
  }));
}

function mapReviewAutomation(run: TicketsBuildRun): TicketsExecutionRuntime['reviewAutomation'] {
  if (!run.reviewAutomation) return undefined;
  return {
    status: String(run.reviewAutomation.status ?? '').trim(),
    message: String(run.reviewAutomation.message ?? '').trim() || undefined,
    updatedAt: run.reviewAutomation.updatedAt,
  };
}

function mapMergeReadiness(run: TicketsBuildRun): TicketsExecutionRuntime['mergeReadiness'] {
  if (!run.mergeReadiness) return undefined;
  const confidence = run.mergeReadiness.confidence;
  return {
    state: String(run.mergeReadiness.state ?? '').trim(),
    score: typeof run.mergeReadiness.score === 'number' ? run.mergeReadiness.score : undefined,
    bugbotRequested: !!run.mergeReadiness.bugbotRequested,
    bugbotIdentity: run.mergeReadiness.bugbotIdentity
      ? {
          appName: String(run.mergeReadiness.bugbotIdentity.appName ?? '').trim() || undefined,
          reviewerLogin: String(run.mergeReadiness.bugbotIdentity.reviewerLogin ?? '').trim() || undefined,
          checkAppNames: Array.isArray(run.mergeReadiness.bugbotIdentity.checkAppNames)
            ? run.mergeReadiness.bugbotIdentity.checkAppNames.map((value: any) => String(value ?? '').trim()).filter(Boolean)
            : undefined,
        }
      : undefined,
    bugbotApprovalState: String(run.mergeReadiness.bugbotApprovalState ?? '').trim() || undefined,
    requiredChecksState: String(run.mergeReadiness.requiredChecksState ?? '').trim() || undefined,
    blockerReason: String(run.mergeReadiness.blockerReason ?? '').trim() || undefined,
    retryCount: typeof run.mergeReadiness.retryCount === 'number' ? run.mergeReadiness.retryCount : undefined,
    updatedAt: run.mergeReadiness.updatedAt,
    confidence: confidence
      ? {
          skillId: String(confidence.skillId ?? '').trim() || undefined,
          ownerRepo: String(confidence.ownerRepo ?? '').trim() || undefined,
          score: typeof confidence.score === 'number' ? confidence.score : 0,
          verdict: confidence.verdict,
          reason: String(confidence.reason ?? '').trim(),
          signals: confidence.signals
            ? {
                total: confidence.signals.total,
                positives: confidence.signals.positives,
                negatives: confidence.signals.negatives,
                neutrals: confidence.signals.neutrals,
                distilledRatio: confidence.signals.distilledRatio,
                firstPassShare: confidence.signals.firstPassShare,
                lastPositiveAgeMs: confidence.signals.lastPositiveAgeMs,
                lastNegativeAgeMs: confidence.signals.lastNegativeAgeMs,
              }
            : undefined,
          computedAt: confidence.computedAt,
        }
      : undefined,
  };
}

export function buildExecutionRuntimeFromRun(
  run: TicketsBuildRun,
  options: { modelFallback?: string; currentModel?: string; liveFollowConnected: boolean }
): Partial<TicketsExecutionRuntime> {
  const currentItem = pickCurrentRunItem(run);
  const active = run.status === 'queued' || run.status === 'planning' || run.status === 'running';
  return {
    active,
    mode: run.kind === 'build' ? 'build' : 'ticket',
    runId: run.id,
    ticketId: String(run.currentTicketId ?? currentItem?.ticketId ?? '').trim(),
    ticketTitle: String(run.currentTicketTitle ?? currentItem?.title ?? '').trim(),
    repoFullName: currentItem?.owner && currentItem?.repo ? `${currentItem.owner}/${currentItem.repo}` : '',
    model: String(run.model ?? options.modelFallback ?? options.currentModel ?? '').trim(),
    phase: (String(run.phase ?? run.status ?? 'running').trim() as TicketsExecutionRuntime['phase']) || 'running',
    startedAt: run.startedAt ?? Date.now(),
    pullRequestUrl: String(currentItem?.pullRequestUrl ?? '').trim() || undefined,
    progress: run.totals,
    workUnits: mapWorkUnits(currentItem),
    latestCommands: Array.isArray(currentItem?.latestCommands) ? currentItem.latestCommands.slice(-8) : undefined,
    latestArtifacts: Array.isArray(currentItem?.latestArtifacts) ? currentItem.latestArtifacts.slice(-8) : undefined,
    plannerRepoConfidence: mapPlannerRepoConfidence(currentItem),
    reviewAutomation: mapReviewAutomation(run),
    mergeReadiness: mapMergeReadiness(run),
    events: Array.isArray(run.events) ? run.events.slice(-100) : undefined,
    latestEventSeq: typeof run.latestEventSeq === 'number' ? run.latestEventSeq : undefined,
    liveFollowConnected: options.liveFollowConnected,
    error:
      String(run.pendingDecision?.prompt ?? '').trim() ||
      String(run.error ?? currentItem?.error ?? '').trim() ||
      undefined,
  };
}
